import { toaster } from '@/components/ui/toaster';
import { programTrackingQueryKey } from '@/hooks/queries/use-program-tracking-query';
import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  PreviewProgramTrackingRequest,
  ProgramPostbackCredential,
  ProgramTrackingPreview,
  ProgramTrackingSettings,
  ProgramTrackingUpdateResult,
  UpdateProgramTrackingRequest,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import i18next from 'i18next';

const unwrap = <T>(response: ApiResponse<T>): T => {
  if (!response.success || !response.data) throw new Error(response.message);
  return response.data;
};

const errorDescription = (error: AxiosError<ApiResponse>, fallbackKey: string) =>
  error.response?.data?.message || i18next.t(fallbackKey);

export const usePreviewProgramTracking = (
  programId: string,
  { notifyOnError = true }: { notifyOnError?: boolean } = {}
) =>
  useMutation<ProgramTrackingPreview, AxiosError<ApiResponse>, PreviewProgramTrackingRequest>({
    mutationFn: async (input) => {
      const { data } = await api.post<ApiResponse<ProgramTrackingPreview>>(
        `/dashboard/programs/${programId}/tracking/preview`,
        input
      );
      return unwrap(data);
    },
    onError: (error) => {
      if (!notifyOnError) return;
      toaster.error({
        title: i18next.t('common.error'),
        description: errorDescription(error, 'programs.programOverview.tracking.previewError'),
      });
    },
  });

export const useUpdateProgramTracking = (brandId: string, programId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    ProgramTrackingUpdateResult,
    AxiosError<ApiResponse>,
    UpdateProgramTrackingRequest
  >({
    mutationFn: async (input) => {
      const { data } = await api.put<ApiResponse<ProgramTrackingUpdateResult>>(
        `/dashboard/programs/${programId}/tracking`,
        input
      );
      return unwrap(data);
    },
    onSuccess: ({ config, externalProgramId }) => {
      const queryKey = programTrackingQueryKey(brandId, programId);
      queryClient.setQueryData<ProgramTrackingSettings>(queryKey, (current) =>
        current
          ? {
              ...current,
              config,
              externalProgramId,
              postback:
                current.externalProgramId === externalProgramId
                  ? current.postback
                  : { ...current.postback, redactedUrl: null },
            }
          : current
      );
      void queryClient.invalidateQueries({ queryKey });
      toaster.success({ title: i18next.t('programs.programOverview.tracking.saved') });
    },
    onError: (error) => {
      toaster.error({
        title: i18next.t('common.error'),
        description: errorDescription(error, 'programs.programOverview.tracking.saveError'),
      });
    },
  });
};

const useProgramCredentialMutation = (brandId: string, programId: string) => {
  const queryClient = useQueryClient();
  return useMutation<ProgramPostbackCredential, AxiosError<ApiResponse>, void>({
    gcTime: 0,
    mutationFn: async () => {
      const { data } = await api.post<ApiResponse<ProgramPostbackCredential>>(
        `/dashboard/programs/${programId}/tracking/postback-credential`
      );
      return unwrap(data);
    },
    onSuccess: () => {
      queryClient.setQueryData<ProgramTrackingSettings>(
        programTrackingQueryKey(brandId, programId),
        (current) =>
          current ? { ...current, postback: { ...current.postback, configured: true } } : current
      );
    },
    onError: (error) => {
      toaster.error({
        title: i18next.t('common.error'),
        description: errorDescription(error, 'programs.programOverview.tracking.credentialError'),
      });
    },
  });
};

export const useProgramPostbackCredential = (brandId: string, programId: string) =>
  useProgramCredentialMutation(brandId, programId);
